import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BadgeCheck, Building2, Store, FileText, CreditCard, type LucideIcon } from 'lucide-react'
import posthog from 'posthog-js'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import GoogleBusinessProfileSection from '@/components/GoogleBusinessProfileSection'
import StorefrontIdentitySection from '@/components/StorefrontIdentitySection'
import StorefrontContentSection from '@/components/StorefrontContentSection'
import BrandColourSection from '@/components/BrandColourSection'
import CurrencySection from '@/components/CurrencySection'

interface DomainInfo {
  username: string | null
  // Legacy field kept on the API response for backwards compatibility.
  // Not rendered on the UI — no wildcard DNS exists for *.tixxly.com so the
  // value is a fictional string. iter-17c drops it from the render path.
  subdomain: string | null
  storefrontUrl: string | null
  customDomain: string | null
  customDomainStatus: string | null
}

interface MerchantDetails {
  merchant_name: string
  website_url: string
  logo_url: string
  contact_email: string
  contact_phone: string
  return_policy_url: string
  return_window_days: number
  terms_url: string
  privacy_url: string
}

const EMPTY_MERCHANT: MerchantDetails = {
  merchant_name: '',
  website_url: '',
  logo_url: '',
  contact_email: '',
  contact_phone: '',
  return_policy_url: '',
  return_window_days: 1,
  terms_url: '',
  privacy_url: '',
}

type SettingsTab = 'business' | 'identity' | 'storefront' | 'content' | 'payments'

const SETTINGS_TABS: { id: SettingsTab; label: string; icon: LucideIcon; description: string }[] = [
  { id: 'business', label: 'Business', icon: Building2, description: 'Merchant details, policies and your Google Business Profile.' },
  { id: 'identity', label: 'Identity', icon: BadgeCheck, description: 'Trading name, destinations, languages, social profiles and press, shown to AI assistants and search engines.' },
  { id: 'storefront', label: 'Storefront', icon: Store, description: 'Your storefront address and brand colour.' },
  { id: 'content', label: 'Content', icon: FileText, description: 'Descriptions, hero, pricing display and FAQs shown on your storefront.' },
  { id: 'payments', label: 'Payments', icon: CreditCard, description: 'The currency your prices are shown and charged in.' },
]

function isSettingsTab(value: string | null): value is SettingsTab {
  return SETTINGS_TABS.some((t) => t.id === value)
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '0.625rem 1.25rem',
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  background: active ? 'var(--color-tixxly-navy)' : 'transparent',
  color: active ? 'var(--color-pure-white)' : 'var(--color-gray-500)',
  border: active ? '1px solid var(--color-tixxly-navy)' : '1px solid var(--color-gray-200)',
  borderRadius: '6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem',
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

export default function SettingsPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId

  // Tab lives in the URL (?tab=payments) so sections can be linked to directly.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: SettingsTab = isSettingsTab(tabParam) ? tabParam : 'business'
  const setTab = (tab: SettingsTab) => {
    setSearchParams(tab === 'business' ? {} : { tab }, { replace: true })
  }

  const [domain, setDomain] = useState<DomainInfo | null>(null)
  const [username, setUsername] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Merchant details state
  const [merchant, setMerchant] = useState<MerchantDetails>(EMPTY_MERCHANT)
  const [merchantLoading, setMerchantLoading] = useState(true)
  const [merchantSaving, setMerchantSaving] = useState(false)
  const [merchantError, setMerchantError] = useState<string | null>(null)
  const [merchantSuccess, setMerchantSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!vendorId) return

    api.vendors.getDomain(vendorId)
      .then((res) => {
        const data = res.data?.data || res.data
        setDomain(data)
        if (data?.username) {
          setUsername(data.username)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))

    // Merchant details: prefer the vendor's saved feed_configuration, but if
    // it's empty (typical for a vendor that has never opened this page before),
    // fall back to seeding from the canonical vendor row so the form doesn't
    // render three empty required fields with a greyed-out Save button.
    //
    // Fallback order: feed_configuration → canonical vendor → EMPTY_MERCHANT.
    Promise.all([
      api.vendors.getFeedConfiguration(vendorId).catch(() => null),
      api.vendors.get(vendorId).catch(() => null),
    ])
      .then(([fcRes, vRes]) => {
        const fc = fcRes?.data?.data?.feed_configuration
        if (fc && Object.keys(fc).length > 0) {
          setMerchant({ ...EMPTY_MERCHANT, ...fc })
          return
        }

        // Seed from canonical vendor row.
        const vendor = vRes?.data?.data || vRes?.data
        if (vendor) {
          const seeded: MerchantDetails = {
            ...EMPTY_MERCHANT,
            merchant_name: vendor.companyName || vendor.company_name || '',
            website_url: vendor.website || '',
            logo_url: vendor.logoUrl || vendor.logo_url
              || vendor.settings?.branding_settings?.logo || '',
            contact_email: vendor.contactInfo?.email || vendor.contact_info?.email || '',
            contact_phone: vendor.contactInfo?.phone || vendor.contact_info?.phone || '',
          }
          setMerchant(seeded)
        }
      })
      .finally(() => setMerchantLoading(false))
  }, [vendorId])

  const handleSave = async () => {
    if (!vendorId || !username.trim()) return

    setError(null)
    setSuccess(null)
    setSaving(true)

    try {
      const res = await api.vendors.setUsername(vendorId, username.trim().toLowerCase())
      const data = res.data?.data || res.data
      setDomain((prev) => ({
        ...prev,
        username: data.username || username,
        subdomain: null,
        storefrontUrl: `https://stores.tixxly.ai/en/v/${username}`,
        customDomain: prev?.customDomain || null,
        customDomainStatus: prev?.customDomainStatus || null,
      }))
      posthog.capture('username_saved', { vendor_id: vendorId, username: data.username || username })
      setSuccess('Username saved successfully')
    } catch (err: any) {
      const message = err.response?.data?.error?.message
        || err.response?.data?.message
        || 'Failed to save username'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleMerchantSave = async () => {
    if (!vendorId) return

    setMerchantError(null)
    setMerchantSuccess(null)
    setMerchantSaving(true)

    try {
      await api.vendors.updateFeedConfiguration(vendorId, merchant)
      posthog.capture('merchant_details_saved', { vendor_id: vendorId })
      setMerchantSuccess('Merchant details saved successfully')
    } catch (err: any) {
      const message = err.response?.data?.error?.message
        || err.response?.data?.message
        || 'Failed to save merchant details'
      setMerchantError(message)
    } finally {
      setMerchantSaving(false)
    }
  }

  const updateMerchant = (field: keyof MerchantDetails, value: string | number) => {
    setMerchant((prev) => ({ ...prev, [field]: value }))
    setMerchantError(null)
    setMerchantSuccess(null)
  }

  if (!vendorId) {
    return (
      <div className="container">
        <h1>Settings</h1>
        <p>Vendor context required.</p>
      </div>
    )
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ marginBottom: '0.25rem' }}>Settings</h1>
        <p style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)', margin: 0 }}>
          {SETTINGS_TABS.find((t) => t.id === activeTab)?.description}
        </p>
      </div>

      <div role="tablist" aria-label="Settings sections" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.75rem', flexWrap: 'wrap' }}>
        {SETTINGS_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`settings-tab-${id}`}
            aria-selected={activeTab === id}
            aria-controls={`settings-panel-${id}`}
            onClick={() => setTab(id)}
            style={tabStyle(activeTab === id)}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`settings-panel-${activeTab}`}
        aria-labelledby={`settings-tab-${activeTab}`}
        style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '760px' }}
      >
        {activeTab === 'business' && (
          <>
            {/* Merchant Details */}
            <div className="card">
              <h3 style={{ marginBottom: '0.5rem' }}>Merchant Details</h3>
              <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                Business information shown on your storefront and AI product feeds.
              </p>

              {merchantLoading ? (
                <p>Loading...</p>
              ) : (
                <>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={labelStyle}>Business Name *</label>
                      <input type="text" value={merchant.merchant_name} onChange={(e) => updateMerchant('merchant_name', e.target.value)} placeholder="Your Business Name" style={inputStyle} />
                    </div>

                    <div>
                      <label style={labelStyle}>Website URL *</label>
                      <input type="url" value={merchant.website_url} onChange={(e) => updateMerchant('website_url', e.target.value)} placeholder="https://www.example.com" style={inputStyle} />
                    </div>

                    <div>
                      <label style={labelStyle}>Logo URL</label>
                      <input type="url" value={merchant.logo_url} onChange={(e) => updateMerchant('logo_url', e.target.value)} placeholder="https://www.example.com/logo.png" style={inputStyle} />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
                      <div>
                        <label style={labelStyle}>Contact Email *</label>
                        <input type="email" value={merchant.contact_email} onChange={(e) => updateMerchant('contact_email', e.target.value)} placeholder="hello@example.com" style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Contact Phone</label>
                        <input type="tel" value={merchant.contact_phone} onChange={(e) => updateMerchant('contact_phone', e.target.value)} placeholder="+44 20 1234 5678" style={inputStyle} />
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Return / Cancellation Policy URL *</label>
                      <input type="url" value={merchant.return_policy_url} onChange={(e) => updateMerchant('return_policy_url', e.target.value)} placeholder="https://www.example.com/cancellation-policy" style={inputStyle} />
                    </div>

                    <div>
                      <label style={labelStyle}>Cancellation Window (days)</label>
                      <input type="number" value={merchant.return_window_days} onChange={(e) => updateMerchant('return_window_days', parseInt(e.target.value, 10) || 0)} min={0} max={365} style={{ ...inputStyle, width: '120px' }} />
                    </div>

                    <div>
                      <label style={labelStyle}>Terms of Service URL *</label>
                      <input type="url" value={merchant.terms_url} onChange={(e) => updateMerchant('terms_url', e.target.value)} placeholder="https://www.example.com/terms" style={inputStyle} />
                    </div>

                    <div>
                      <label style={labelStyle}>Privacy Policy URL *</label>
                      <input type="url" value={merchant.privacy_url} onChange={(e) => updateMerchant('privacy_url', e.target.value)} placeholder="https://www.example.com/privacy" style={inputStyle} />
                    </div>
                  </div>

                  {merchantError && (
                    <p style={{ color: 'var(--color-error)', marginTop: '1rem', fontSize: '0.9rem' }}>
                      {merchantError}
                    </p>
                  )}

                  {merchantSuccess && (
                    <p style={{ color: 'var(--color-success)', marginTop: '1rem', fontSize: '0.9rem' }}>
                      {merchantSuccess}
                    </p>
                  )}

                  <button
                    onClick={handleMerchantSave}
                    disabled={merchantSaving || !merchant.merchant_name.trim()}
                    style={{
                      marginTop: '1.5rem',
                      padding: '0.5rem 1.5rem',
                      background: merchantSaving ? 'var(--color-gray-400)' : 'var(--color-tixxly-navy)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: merchantSaving ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                    }}
                  >
                    {merchantSaving ? 'Saving...' : 'Save Merchant Details'}
                  </button>
                </>
              )}
            </div>

            <GoogleBusinessProfileSection vendorId={vendorId} />
          </>
        )}

        {/* Storefront Identity (iter-3b: legal/trading/founder + JSON-LD identity fields) */}
        {activeTab === 'identity' && <StorefrontIdentitySection vendorId={vendorId} />}

        {activeTab === 'storefront' && (
          <>
            {/* Storefront Username */}
            <div className="card">
              <h3 style={{ marginBottom: '0.5rem' }}>Storefront URL</h3>
              <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                Set your unique storefront name. This is how customers and AI agents find your experiences.
              </p>

              {loading ? (
                <p>Loading...</p>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <span style={{ color: 'var(--color-gray-500)', fontSize: '0.9rem' }}>
                      stores.tixxly.ai/v/
                    </span>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                        setError(null)
                        setSuccess(null)
                      }}
                      placeholder="your-business-name"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        border: '1px solid var(--color-gray-300)',
                        borderRadius: '6px',
                        fontSize: '1rem',
                      }}
                      maxLength={63}
                      minLength={3}
                    />
                  </div>

                  <p style={{ fontSize: '0.8rem', color: 'var(--color-gray-500)', marginBottom: '1rem' }}>
                    Lowercase letters, numbers, and hyphens only. 3-63 characters.
                  </p>

                  {domain?.username && (
                    <div style={{
                      padding: '0.75rem 1rem',
                      background: 'var(--color-gray-50)',
                      borderRadius: '6px',
                      marginBottom: '1rem',
                      fontSize: '0.9rem',
                    }}>
                      <p><strong>Current storefront:</strong>{' '}
                        <a
                          href={`https://stores.tixxly.ai/en/v/${domain.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--color-tixxly-navy)' }}
                        >
                          stores.tixxly.ai/v/{domain.username}
                        </a>
                      </p>
                      {domain.customDomain && (
                        <p style={{ marginTop: '0.25rem' }}>
                          <strong>Custom domain:</strong> {domain.customDomain}
                          {domain.customDomainStatus === 'active'
                            ? ' (active)'
                            : ` (${domain.customDomainStatus || 'pending'})`}
                        </p>
                      )}
                    </div>
                  )}

                  {error && (
                    <p style={{ color: 'var(--color-error)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      {error}
                    </p>
                  )}

                  {success && (
                    <p style={{ color: 'var(--color-success)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      {success}
                    </p>
                  )}

                  <button
                    onClick={handleSave}
                    disabled={saving || !username.trim() || username.length < 3}
                    style={{
                      padding: '0.5rem 1.5rem',
                      background: saving ? 'var(--color-gray-400)' : 'var(--color-tixxly-navy)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                    }}
                  >
                    {saving ? 'Saving...' : domain?.username ? 'Update Username' : 'Set Username'}
                  </button>
                </>
              )}
            </div>

            {/* Brand colour (iter-13a: primary CTA colour applied across the hosted storefront) */}
            <BrandColourSection vendorId={vendorId} />
          </>
        )}

        {/* Storefront v3 content (iter-3c): descriptions, hero, pricing mode, FAQs */}
        {activeTab === 'content' && <StorefrontContentSection vendorId={vendorId} />}

        {/* Selling currency: Bokun prices, storefront and checkout follow settings.currency */}
        {activeTab === 'payments' && <CurrencySection vendorId={vendorId} />}
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.35rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: 'var(--color-gray-700)',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '0.9rem',
}
